import React, { useState, useCallback } from 'react';
import { Box, Card, CardContent, Typography, TextField, Button, Grid, Checkbox, FormControlLabel, FormGroup, Select, MenuItem, FormControl, InputLabel, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Chip, LinearProgress, Alert, Tooltip } from '@mui/material';
import { Search, TableChart, PictureAsPdf, Description, FilterList, AutoAwesome } from '@mui/icons-material';
import api from '../../services/api';

const CATEGORIES = [
  { key: 'software', label: 'Software' },
  { key: 'sistema_informatico', label: 'Sistemas Informaticos' },
  { key: 'desarrollo', label: 'Desarrollo' },
  { key: 'licenciamiento', label: 'Licenciamiento' },
  { key: 'cloud', label: 'Cloud' },
  { key: 'firewall', label: 'Firewall' },
  { key: 'ciberseguridad', label: 'Ciberseguridad' },
  { key: 'seguridad', label: 'Seguridad' },
  { key: 'servidor', label: 'Servidores' },
  { key: 'data_center', label: 'Data Center' },
  { key: 'monitoreo', label: 'Monitoreo' },
  { key: 'voip', label: 'VoIP' },
  { key: 'telefonia', label: 'Telefonia' },
  { key: 'erp', label: 'ERP' },
  { key: 'ia', label: 'Inteligencia Artificial' },
];

const MONTOS = [
  { value: 1000000, label: '$1M' },
  { value: 2000000, label: '$2M' },
  { value: 3000000, label: '$3M' },
  { value: 5000000, label: '$5M' },
  { value: 7000000, label: '$7M' },
  { value: 10000000, label: '$10M' },
  { value: 15000000, label: '$15M' },
  { value: 20000000, label: '$20M' },
  { value: 25000000, label: '$25M' },
  { value: 30000000, label: '$30M' },
];

export default function LicitacionesPage() {
  const [categories, setCategories] = useState([]);
  const [montoMin, setMontoMin] = useState('');
  const [montoMax, setMontoMax] = useState('');
  const [fechaInicio, setFechaInicio] = useState('');
  const [fechaFin, setFechaFin] = useState('');
  const [searchText, setSearchText] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalResults, setTotalResults] = useState(0);
  const [error, setError] = useState('');
  const [prevResults, setPrevResults] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiProgress, setAiProgress] = useState('');
  const [aiInfo, setAiInfo] = useState(null);

  const toggleCategory = useCallback((key) => {
    setCategories(prev =>
      prev.includes(key) ? prev.filter(c => c !== key) : [...prev, key]
    );
  }, []);

  const handleSearch = useCallback(async () => {
    setLoading(true);
    setError('');
    setResults([]);
    try {
      const params = {};
      params.page = '1';
      params.limit = '50';
      if (searchText.trim()) params.search = searchText.trim();
      if (categories.length > 0) params.categoria = categories.join(',');
      if (montoMin) params.monto_min = String(montoMin);
      if (montoMax) params.monto_max = String(montoMax);
      if (fechaInicio) params.fecha_inicio = fechaInicio;
      if (fechaFin) params.fecha_fin = fechaFin;

      const res = await api.get('/licitaciones', { params });
      const data = res.data.items || [];
      setResults(data);
      setTotalResults(res.data.total || data.length);
    } catch (err) {
      console.error('Error:', err);
      setError(err.response?.data?.detail || err.message || 'Error al buscar');
    }
    setLoading(false);
  }, [categories, montoMin, montoMax, fechaInicio, fechaFin, searchText]);

  const handleAIFilter = useCallback(async () => {
    if (results.length === 0 || aiLoading) return;
    setAiLoading(true);
    setError('');
    setAiInfo(null);
    setPrevResults(results);
    const lotes = Math.max(Math.ceil(results.length / 12), 1);
    let ticks = 0;
    const ticker = setInterval(() => {
      ticks += 1;
      const loteActual = Math.min(Math.ceil((ticks * 12) / results.length), lotes);
      setAiProgress('OpenCode evaluando lote ' + loteActual + ' de ' + lotes + ' (' + results.length + ' licitaciones, modelo big-pickle via Tor)...');
    }, 8000);
    try {
      const datos = results.map(l => ({
        Codigo: l.codigo,
        Nombre: l.nombre,
        Organismo: l.organismo,
        Descripcion: l.descripcion || '',
        fecha_cierre: l.fecha_cierre || null,
        monto_estimado: l.monto_estimado || null,
        categoria: l.categoria || null,
      }));
      const res = await api.post('/licitaciones/filtrar-opencode', {
        datos_entrada: datos,
        umbral: 70,
        batch_size: 12,
        timeout_lote: 150,
      }, { timeout: 900000 });
      const data = res.data || {};
      if (data.status === 'success') {
        // Merge: preserva TODOS los atributos descriptivos de la fila original
        // (codigo, nombre, organismo, fechas, montos) y solo inyecta ai_score/ai_categoria/ai_justificacion.
        // Los items del endpoint pueden traer claves Capitalizadas (Codigo/Nombre/Organismo);
        // aqui se normalizan sobre la forma canonica minuscula que la tabla espera.
        const baseById = {};
        results.forEach(r0 => {
          if (r0 && r0.codigo !== undefined && r0.codigo !== null) baseById[String(r0.codigo)] = r0;
        });
        const merged = (data.items || []).map(it => {
          const key = String(it.Codigo !== undefined && it.Codigo !== null ? it.Codigo : (it.codigo !== undefined && it.codigo !== null ? it.codigo : it.id));
          const base = baseById[key] || {};
          const out = { ...base, ...it };
          out.codigo = base.codigo !== undefined ? base.codigo : (it.Codigo !== undefined ? it.Codigo : it.codigo);
          out.nombre = base.nombre !== undefined ? base.nombre : (it.Nombre !== undefined ? it.Nombre : it.nombre);
          out.organismo = base.organismo !== undefined ? base.organismo : (it.Organismo !== undefined ? it.Organismo : it.organismo);
          return out;
        });
        setResults(merged);
        setTotalResults(data.total_coincidentes || merged.length);
        const r = data.resumen || {};
        setAiInfo('Filtro IA (OpenCode/' + (r.modelo || 'big-pickle') + '): ' + data.total_coincidentes + ' de ' + data.total_procesados + ' licitaciones pertinentes al perfil de negocio TI (umbral ' + (r.umbral_score !== undefined ? r.umbral_score : 70) + ')'
          + (r.descartadas !== undefined ? ' \u00b7 descartadas ' + r.descartadas : '')
          + (r.errores_evaluacion ? ' \u00b7 errores de evaluaci\u00f3n: ' + r.errores_evaluacion : ''));
      } else {
        setError('Respuesta inesperada del filtro IA');
      }
    } catch (err) {
      console.error('Error filtro IA:', err);
      setError((err.response && err.response.data && err.response.data.detail) || err.message || 'Error en el filtro IA');
    } finally {
      clearInterval(ticker);
      setAiLoading(false);
      setAiProgress('');
    }
  }, [results, aiLoading]);

  const handleRestoreResults = useCallback(() => {
    if (prevResults) {
      setResults(prevResults);
      setTotalResults(prevResults.length);
      setAiInfo(null);
      setPrevResults(null);
    }
  }, [prevResults]);

  const handleExport = async (format) => {
    try {
      const params = { format };
      if (searchText.trim()) params.search = searchText.trim();
      if (categories.length > 0) params.categoria = categories.join(',');
      if (montoMin) params.monto_min = parseFloat(montoMin);
      if (montoMax) params.monto_max = parseFloat(montoMax);
      if (fechaInicio) params.fecha_inicio = fechaInicio;
      if (fechaFin) params.fecha_fin = fechaFin;

      const esVistaIA = results.some(l => l.ai_score !== undefined);
      let payload = params;
      // Sincronizacion con la vista: si el Filtro IA esta activo, exportar
      // exactamente el dataset filtrado visible (results) con sus columnas IA,
      // no la consulta cruda original.
      if (esVistaIA) {
        payload = {
          format,
          datos_entrada: results.map(l => ({
            Codigo: l.codigo,
            Nombre: l.nombre,
            Organismo: l.organismo,
            fecha_cierre: l.fecha_cierre || null,
            categoria: l.categoria || null,
            ai_score: l.ai_score,
            ai_categoria: l.ai_categoria || '',
            ai_justificacion: l.ai_justificacion || '',
          })),
        };
      }

      const res = await api.post('/licitaciones/export', payload, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      const ext = format === 'excel' ? 'xlsx' : format;
      const sufijo = esVistaIA ? '_filtro_ia' : '';
      a.download = `licitaciones${sufijo}_${new Date().toISOString().slice(0,10)}.${ext}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error exportando:', err);
    }
  };

  const mostrarIA = results.some(l => l.ai_score !== undefined);

  return (
    <Box>
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <FilterList color="primary" />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>Filtros de Busqueda</Typography>
          </Box>

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Categorias</Typography>
              <FormGroup row>
                {CATEGORIES.map(cat => (
                  <FormControlLabel key={cat.key}
                    control={<Checkbox checked={categories.includes(cat.key)}
                      onChange={() => toggleCategory(cat.key)} size="small"
                      sx={{ color: '#0047AB', '&.Mui-checked': { color: '#00A3FF' } }} />}
                    label={cat.label} />
                ))}
              </FormGroup>
            </Grid>

            <Grid item xs={12} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Monto Minimo</InputLabel>
                <Select value={montoMin} label="Monto Minimo"
                  onChange={e => setMontoMin(e.target.value)}>
                  <MenuItem value="">Sin minimo</MenuItem>
                  {MONTOS.map(m => <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Monto Maximo</InputLabel>
                <Select value={montoMax} label="Monto Maximo"
                  onChange={e => setMontoMax(e.target.value)}>
                  <MenuItem value="">Sin maximo</MenuItem>
                  {MONTOS.map(m => <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={2}>
              <TextField fullWidth size="small" type="date" label="Fecha Inicio"
                value={fechaInicio} InputLabelProps={{ shrink: true }}
                onChange={e => setFechaInicio(e.target.value)} />
            </Grid>

            <Grid item xs={12} md={2}>
              <TextField fullWidth size="small" type="date" label="Fecha Fin"
                value={fechaFin} InputLabelProps={{ shrink: true }}
                onChange={e => setFechaFin(e.target.value)} />
            </Grid>

            <Grid item xs={12} md={8}>
              <TextField fullWidth size="small" label="Buscar por nombre o codigo"
                value={searchText} onChange={e => setSearchText(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') handleSearch(); }} />
            </Grid>

            <Grid item xs={12} md={4}>
              <Button variant="contained" startIcon={<Search />} onClick={handleSearch}
                disabled={loading} size="large" fullWidth sx={{ py: 1.5 }}>
                {loading ? 'Buscando...' : 'Buscar'}
              </Button>
            </Grid>
          </Grid>

          {loading && (
            <Box sx={{ mt: 2 }}>
              <LinearProgress variant="indeterminate" sx={{
                height: 6, borderRadius: 3,
                '& .MuiLinearProgress-bar': { background: 'linear-gradient(90deg, #0047AB, #00A3FF)' }
              }} />
            </Box>
          )}
          {aiLoading && (
            <Box sx={{ mt: 2 }}>
              <LinearProgress variant="indeterminate" sx={{
                height: 6, borderRadius: 3,
                '& .MuiLinearProgress-bar': { background: 'linear-gradient(90deg, #7B1FA2, #E040FB)' }
              }} />
              <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
                {aiProgress || 'Conectando con el motor OpenCode...'}
              </Typography>
            </Box>
          )}
          {aiInfo && !aiLoading && (
            <Alert severity="success" sx={{ mt: 2 }}
              action={<Button color="inherit" size="small" onClick={handleRestoreResults}>Ver todos</Button>}>
              {aiInfo}
            </Alert>
          )}
        </CardContent>
      </Card>

      {results.length > 0 && (
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Resultados ({totalResults})
              </Typography>
              <Box>
                <Button variant="outlined" startIcon={<TableChart />} onClick={() => handleExport('excel')} sx={{ mr: 1 }}>
                  Excel
                </Button>
                <Button variant="outlined" startIcon={<PictureAsPdf />} onClick={() => handleExport('pdf')} sx={{ mr: 1 }}>
                  PDF
                </Button>
                <Button variant="outlined" startIcon={<Description />} onClick={() => handleExport('csv')}>
                  CSV
                </Button>
                <Button variant="outlined" startIcon={<AutoAwesome />} onClick={handleAIFilter}
                  disabled={aiLoading || loading} sx={{ color: '#7B1FA2', borderColor: '#7B1FA2', mr: 1 }}>
                  {aiLoading ? 'Evaluando con IA...' : 'Filtro IA (OpenCode)'}
                </Button>
              </Box>
            </Box>

            <TableContainer component={Paper} sx={{ maxHeight: 500 }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>Codigo</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Nombre</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Organismo</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Fecha Cierre</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Categoria</TableCell>
                    {mostrarIA && <TableCell sx={{ fontWeight: 700 }}>Evaluacion IA</TableCell>}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {results.map((l) => (
                    <TableRow key={l.codigo} hover sx={{ cursor: 'pointer' }}
                      onClick={() => window.location.href = `/licitaciones/${l.codigo}`}>
                      <TableCell><Chip label={l.codigo} size="small" color="primary" variant="outlined" /></TableCell>
                      <TableCell sx={{ maxWidth: 300 }} noWrap>{l.nombre}</TableCell>
                      <TableCell sx={{ maxWidth: 200 }} noWrap>{l.organismo || '-'}</TableCell>
                      <TableCell>{l.fecha_cierre ? new Date(l.fecha_cierre).toLocaleDateString('es-CL') : '-'}</TableCell>
                      <TableCell>
                        <Chip label={l.categoria || 'N/A'} size="small" color={l.categoria ? 'primary' : 'default'} />
                      </TableCell>
                      {mostrarIA && (
                        <TableCell>
                          {l.ai_score !== undefined ? (
                            <Tooltip title={l.ai_justificacion || 'Sin justificacion'} arrow placement="top">
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Chip size="small" color={l.ai_score >= 90 ? 'success' : 'primary'} label={'IA ' + l.ai_score} />
                                <Typography variant="caption" sx={{ maxWidth: 140 }} noWrap>{l.ai_categoria}</Typography>
                              </Box>
                            </Tooltip>
                          ) : (
                            <Typography variant="caption" color="text.secondary">-</Typography>
                          )}
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      {!loading && results.length === 0 && !error && (
        <Card><CardContent>
          <Typography variant="body1" align="center" sx={{ py: 6, color: 'text.secondary' }}>
            Seleccione categorias y presione "Buscar" para encontrar licitaciones
          </Typography>
        </CardContent></Card>
      )}
    </Box>
  );
}
